import React, { useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

/**
 * Read-only-ish table of pending cash conversions with inline approve / reject
 * actions. Shows the projected pool impact and the last approved rate as
 * order-of-magnitude sanity cues per the spec.
 */
const CashConversionTable = ({
  rows,
  direction, // 'inr_to_usdt' | 'usdt_to_inr'
  endpointBase,
  currentUser,
  currentPoolBalance,
  lastApprovedRate,
  onActioned,
}) => {
  const [busyId, setBusyId] = useState(null);
  const [selfReasonModal, setSelfReasonModal] = useState({ open: false, row: null });

  const isInrOut = direction === 'inr_to_usdt';

  const projectedBalance = (row) => {
    const delta = isInrOut ? -Number(row.amount_inr || 0) : Number(row.inr_received || 0);
    return Number(currentPoolBalance || 0) + delta;
  };

  const doAction = async (row, action, body = {}) => {
    setBusyId(row.id);
    try {
      const r = await axiosInstance.post(`${endpointBase}/${row.id}/${action}`, body);
      toast.success(r.data?.message || `Conversion ${action}d`);
      onActioned && onActioned();
    } catch (err) {
      const detail = err.response?.data?.detail || `Failed to ${action}`;
      if (err.response?.status === 403 && detail.includes('self_approval_reason')) {
        setSelfReasonModal({ open: true, row });
      } else {
        toast.error(detail);
      }
    } finally {
      setBusyId(null);
    }
  };

  const submitSelfReason = async (reason) => {
    const row = selfReasonModal.row;
    setSelfReasonModal({ open: false, row: null });
    if (!reason || !reason.trim()) return;
    await doAction(row, 'approve', { self_approval_reason: reason.trim() });
  };

  const rejectWithNote = async (row) => {
    const note = window.prompt('Reason for rejection (optional):', '');
    if (note === null) return; // cancelled
    await doAction(row, 'reject', { review_note: note });
  };

  return (
    <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
      <table className="cb-table" style={{ minWidth: '100%' }} data-testid={`cash-conv-table-${direction}`}>
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Direction</th>
            <th className="cb-num">Amount</th>
            <th className="cb-num">Rate</th>
            <th className="cb-num">Counter</th>
            <th className="cb-num">Projected pool</th>
            <th>Notes</th>
            <th>By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isMine = r.created_by === currentUser?.user_id;
            const proj = projectedBalance(r);
            const rateSuspect = lastApprovedRate && Math.abs(r.conversion_rate - lastApprovedRate) / lastApprovedRate > 0.2;
            return (
              <tr key={r.id} data-testid={`cash-conv-row-${r.id}`}>
                <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                <td><span className="cb-pill">{isInrOut ? 'INR → USDT' : 'USDT → INR'}</span></td>
                <td className="cb-num">
                  {isInrOut
                    ? `₹${Number(r.amount_inr).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                    : `${Number(r.usdt_amount).toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT`}
                </td>
                <td className="cb-num">
                  {r.conversion_rate}
                  {lastApprovedRate ? (
                    <span
                      className="cb-muted"
                      style={{ marginLeft: 4, fontSize: '0.6875rem', color: rateSuspect ? 'var(--cb-warning, #b47a00)' : undefined }}
                      title={`Last approved rate: ${lastApprovedRate}`}
                      data-testid={`cash-conv-rate-hint-${r.id}`}
                    >
                      {rateSuspect ? '⚠ ' : ''}(last: {lastApprovedRate})
                    </span>
                  ) : null}
                </td>
                <td className="cb-num">
                  {isInrOut
                    ? `${Number(r.usdt_amount).toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT`
                    : `₹${Number(r.inr_received).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                </td>
                <td className="cb-num" data-testid={`cash-conv-projected-${r.id}`}>
                  <span className="cb-muted" style={{ fontSize: '0.75rem' }}>₹{Number(currentPoolBalance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} → </span>
                  <strong>₹{Number(proj).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong>
                </td>
                <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{r.notes || '-'}</td>
                <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>
                  {r.created_by === currentUser?.user_id ? (<em>you</em>) : (r.created_by?.slice(0, 8) || '-')}
                </td>
                <td>
                  <div className="flex gap-2">
                    <button
                      className="cb-btn-primary"
                      disabled={busyId === r.id}
                      onClick={() => isMine ? setSelfReasonModal({ open: true, row: r }) : doAction(r, 'approve')}
                      data-testid={`cash-conv-approve-${r.id}`}
                    >
                      {busyId === r.id ? '…' : 'Approve'}
                    </button>
                    <button
                      className="cb-btn-secondary"
                      disabled={busyId === r.id}
                      onClick={() => rejectWithNote(r)}
                      data-testid={`cash-conv-reject-${r.id}`}
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selfReasonModal.open && (
        <SelfApprovalReasonModal
          onClose={() => setSelfReasonModal({ open: false, row: null })}
          onSubmit={submitSelfReason}
        />
      )}
    </div>
  );
};

const SelfApprovalReasonModal = ({ onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="cash-conv-self-approval-modal">
      <div className="cb-card" style={{ width: '100%', maxWidth: 480, padding: 'var(--cb-space-6)' }}>
        <p className="cb-eyebrow">Self-approval guard</p>
        <h4 className="cb-section-title" style={{ marginTop: 4 }}>You submitted this conversion</h4>
        <p className="cb-body" style={{ margin: '12px 0' }}>Enter a reason to approve your own submission. This is written to the audit trail.</p>
        <textarea
          className="cb-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          style={{ width: '100%' }}
          data-testid="cash-conv-self-approval-reason-input"
        />
        <div className="flex justify-end gap-2" style={{ marginTop: 12 }}>
          <button type="button" className="cb-btn-secondary" onClick={onClose} data-testid="cash-conv-self-approval-cancel">Cancel</button>
          <button
            type="button"
            className="cb-btn-primary"
            disabled={!reason.trim()}
            onClick={() => onSubmit(reason)}
            data-testid="cash-conv-self-approval-submit"
          >
            Approve with reason
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashConversionTable;
